import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import { Send, Users, User, MessageSquare, X, Check, AlertCircle } from 'lucide-react';

interface MessageTemplate {
  id: number;
  name: string;
  content: string;
  category: string;
  variables?: string[] | string;
}

interface LineMember {
  id: number;
  line_user_id: string;
  line_id?: string; // line.jsからのデータ互換性
  display_name: string;
  name?: string; // line.jsからのデータ互換性
  phone?: string;
  is_blocked?: number; // SQLiteからは0/1で返される
}

interface MessageSenderProps {
  selectedMembers?: LineMember[];
  onClose?: () => void;
}

const MessageSender: React.FC<MessageSenderProps> = ({ selectedMembers = [], onClose }) => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [useTemplate, setUseTemplate] = useState(true);
  const [variables, setVariables] = useState<{ [key: string]: string }>({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; status?: 'success' | 'partial' | 'error'; message: string; details?: { successCount: number; failCount: number; totalSent: number } } | null>(null);
  const [enableNotification, setEnableNotification] = useState(true); // 通知ON/OFF（デフォルトON）

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/message-templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('テンプレート取得エラー:', error);
    }
  };

  const extractVariables = (content: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const vars: string[] = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (!vars.includes(match[1])) {
        vars.push(match[1]);
      }
    }
    return vars;
  };

  const replaceVariables = (content: string): string => {
    let message = content;
    Object.entries(variables).forEach(([key, value]) => {
      message = message.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
    });

    // 共通変数の置き換え
    selectedMembers.forEach(member => {
      message = message.replace(/\{\{customer_name\}\}/g, member.display_name);
      message = message.replace(/\{\{name\}\}/g, member.display_name);
    });

    return message;
  };

  const handleSend = async () => {
    if (selectedMembers.length === 0) {
      alert('送信先を選択してください');
      return;
    }

    const messageContent = useTemplate && selectedTemplate
      ? replaceVariables(selectedTemplate.content)
      : customMessage;

    if (!messageContent.trim()) {
      alert('メッセージを入力してください');
      return;
    }

    setSending(true);
    setResult(null);

    try {
      // line_user_id または line_id を使用（LineMemberManagementDynamicからはline_idが来る）
      const memberIds = selectedMembers.map(m => m.line_user_id || m.line_id || '');

      console.log('送信データ:', {
        memberIds,
        message: messageContent,
        templateId: selectedTemplate?.id,
        enableNotification
      });

      const response = await apiClient.post('/line/send-message', {
        memberIds,
        message: messageContent,
        templateId: selectedTemplate?.id,
        enableNotification
      });

      console.log('送信結果:', response.data);

      const { successCount, failCount, totalSent } = response.data;

      // 詳細なメッセージを作成
      let resultMessage = '';
      let resultStatus: 'success' | 'partial' | 'error' = 'success';
      if (failCount === 0) {
        resultMessage = `✅ ${successCount}名全員にメッセージを送信しました！`;
      } else if (successCount === 0 && failCount > 0) {
        resultStatus = 'error';
        resultMessage = `❌ メッセージ送信に失敗しました（${failCount}件）\nLINE側の送信上限、ブロック、またはAPIエラーの可能性があります`;
      } else {
        resultStatus = 'partial';
        resultMessage = `⚠️ 一部のメッセージ送信に失敗しました\n送信成功: ${successCount}名\n送信失敗: ${failCount}名\n合計: ${totalSent}名\nLINE側の送信上限、ブロック、またはAPIエラーの可能性があります`;
      }

      setResult({
        success: resultStatus === 'success',
        status: resultStatus,
        message: resultMessage,
        details: { successCount, failCount, totalSent }
      });

      // テンプレートの使用回数を更新
      if (selectedTemplate && successCount > 0) {
        await apiClient.post(`/message-templates/${selectedTemplate.id}/use`);
      }

      if (resultStatus === 'success') {
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      }

    } catch (error: any) {
      console.error('送信エラー:', error);
      setResult({
        success: false,
        status: 'error',
        message: error.response?.data?.error || 'メッセージの送信に失敗しました'
      });
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSelect = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    const vars = extractVariables(template.content);
    const newVariables: { [key: string]: string } = {};
    vars.forEach(v => {
      if (v !== 'customer_name' && v !== 'name') {
        newVariables[v] = variables[v] || '';
      }
    });
    setVariables(newVariables);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">LINEメッセージ送信</h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedMembers.length}名に送信予定
              </p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* 送信先一覧 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              送信先
            </h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex flex-wrap gap-2">
                {selectedMembers.map(member => (
                  <span
                    key={member.id}
                    className="px-3 py-1 bg-white rounded-full text-sm flex items-center gap-1"
                  >
                    <User className="w-3 h-3" />
                    {member.display_name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* 通知設定 */}
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">プッシュ通知</p>
                  <p className="text-sm text-blue-700">
                    {enableNotification ? '有効（受信者に通知が届きます）' : '無効（通知なし・サイレント送信）'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEnableNotification(!enableNotification)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  enableNotification ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enableNotification ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* メッセージタイプ選択 */}
          <div className="mb-6">
            <div className="flex gap-4 mb-4">
              <button
                onClick={() => setUseTemplate(true)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  useTemplate
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                テンプレートを使用
              </button>
              <button
                onClick={() => setUseTemplate(false)}
                className={`px-4 py-2 rounded-lg ${
                  !useTemplate
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                カスタムメッセージ
              </button>
            </div>

            {useTemplate ? (
              <>
                {/* テンプレート選択 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    テンプレートを選択
                  </label>
                  <div className="grid gap-2 max-h-60 overflow-y-auto">
                    {templates.map(template => (
                      <div
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedTemplate?.id === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium">{template.name}</p>
                            <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap line-clamp-2">
                              {template.content}
                            </p>
                          </div>
                          {selectedTemplate?.id === template.id && (
                            <Check className="w-5 h-5 text-blue-600 ml-2" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 変数入力 */}
                {selectedTemplate && Object.keys(variables).length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">
                      変数を入力
                    </label>
                    <div className="space-y-2">
                      {Object.keys(variables).map(variable => (
                        <div key={variable}>
                          <label className="block text-xs text-gray-600 mb-1">
                            {`{{${variable}}}`}
                          </label>
                          <input
                            type="text"
                            value={variables[variable]}
                            onChange={(e) => setVariables({
                              ...variables,
                              [variable]: e.target.value
                            })}
                            className="w-full p-2 border rounded-lg text-sm"
                            placeholder={`${variable}を入力`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* プレビュー */}
                {selectedTemplate && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">
                      プレビュー
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">
                        {replaceVariables(selectedTemplate.content)}
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* カスタムメッセージ */
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  メッセージ内容
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="w-full p-3 border rounded-lg h-40"
                  placeholder="送信するメッセージを入力してください"
                />
              </div>
            )}
          </div>

          {/* 結果表示 */}
          {result && (
            <div className={`p-4 rounded-lg ${
              result.status === 'success'
                ? 'bg-green-50 text-green-700'
                : result.status === 'partial'
                ? 'bg-yellow-50 text-yellow-800'
                : 'bg-red-50 text-red-700'
            }`}>
              <div className="flex items-start gap-2">
                {result.status === 'success' ? (
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="whitespace-pre-wrap">{result.message}</p>
                  {result.details && result.details.failCount > 0 && (
                    <p className={`text-xs mt-2 ${
                      result.status === 'partial' ? 'text-yellow-700' : 'text-red-600'
                    }`}>
                      ※ 送信失敗はLINE側の送信上限、ブロック、またはAPIエラーが原因です
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={handleSend}
              disabled={sending || (!selectedTemplate && useTemplate) || (!customMessage && !useTemplate)}
              className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 ${
                sending || (!selectedTemplate && useTemplate) || (!customMessage && !useTemplate)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <Send className="w-5 h-5" />
              {sending ? '送信中...' : 'メッセージを送信'}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-white"
              >
                キャンセル
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageSender;
